import React from 'react';
import { Box } from '@mui/material';

/**
 * Ce composant reçoit une nationalité en prop,
 * puis renvoie un <img> avec le drapeau correspondant si possible.
 * Sinon, il ne renvoie rien (null).
 *
 * Le composant extrait le code ISO à 3 lettres entre parenthèses (ex: "USA", "ESP", "FRA")
 * pour trouver le drapeau correspondant, ce qui le rend indépendant de la langue.
 */
const FlagDisplay = ({ nationality, size = '20px' }) => {
  if (!nationality) {
    return null;
  }

  // Dictionnaire clé de traduction -> code ISO 2 lettres pour flagcdn
  const keyToFlagMap = {
    "afghan": "af", "german": "de", "andorran": "ad", "angolan": "ao", "antiguanBarbudan": "ag",
    "algerian": "dz", "argentine": "ar", "armenian": "am", "australian": "au", "austrian": "at",
    "azerbaijani": "az", "bahamian": "bs", "bahraini": "bh", "bangladeshi": "bd", "barbadian": "bb",
    "belgian": "be", "belizean": "bz", "beninese": "bj", "belarusian": "by", "burmese": "mm",
    "bissauGuinean": "gw", "bolivian": "bo", "bosnian": "ba", "motswana": "bw", "brazilian": "br",
    "britishOverseas": "gb", "bruneian": "bn", "bulgarian": "bg", "burkinabe": "bf", "burundian": "bi",
    "bhutanese": "bt", "capeVerdean": "cv", "cambodian": "kh", "cameroonian": "cm", "canadian": "ca",
    "qatari": "qa", "centralAfrican": "cf", "chadian": "td", "czech": "cz", "chilean": "cl",
    "chinese": "cn", "cypriot": "cy", "colombian": "co", "comorian": "km", "congolese": "cg",
    "congoleseDR": "cd", "costaRican": "cr", "croatian": "hr", "cuban": "cu", "danish": "dk",
    "dominican": "do", "dominiquais": "dm", "ecuadorian": "ec", "egyptian": "eg", "emirati": "ae",
    "eritrean": "er", "slovak": "sk", "slovenian": "si", "spanish": "es", "american": "us",
    "estonian": "ee", "ethiopian": "et", "filipino": "ph", "finnish": "fi", "fijian": "fj",
    "french": "fr", "gabonese": "ga", "gambian": "gm", "georgian": "ge", "ghanaian": "gh",
    "grenadian": "gd", "greek": "gr", "guatemalan": "gt", "guinean": "gn", "guyanese": "gy",
    "haitian": "ht", "dutch": "nl", "honduran": "hn", "hungarian": "hu", "indian": "in",
    "english": "gb", "indonesian": "id", "iranian": "ir", "iraqi": "iq", "irish": "ie",
    "icelandic": "is", "israeli": "il", "italian": "it", "jamaican": "jm", "japanese": "jp",
    "jordanian": "jo", "kazakh": "kz", "kenyan": "ke", "kosovar": "xk", "kyrgyz": "kg", "kiribati": "ki",
    "kuwaiti": "kw", "laotian": "la", "basotho": "ls", "latvian": "lv", "lebanese": "lb",
    "liberian": "lr", "libyan": "ly", "liechtensteiner": "li", "lithuanian": "lt", "luxembourger": "lu",
    "macedonian": "mk", "malaysian": "my", "malawian": "mw", "malagasy": "mg", "malian": "ml",
    "maltese": "mt", "ivorian": "ci", "moroccan": "ma", "mauritian": "mu", "mauritanian": "mr",
    "mexican": "mx", "micronesian": "fm", "moldovan": "md", "monegasque": "mc", "mongolian": "mn",
    "montenegrin": "me", "mozambican": "mz", "namibian": "na", "newZealander": "nz", "nepalese": "np",
    "nicaraguan": "ni", "nigerien": "ne", "nigerian": "ng", "northKorean": "kp", "norwegian": "no",
    "omani": "om", "pakistani": "pk", "palauan": "pw", "panamanian": "pa", "papuaNewGuinean": "pg",
    "paraguayan": "py", "peruvian": "pe", "polish": "pl", "portuguese": "pt", "puertoRican": "pr",
    "reunionese": "re", "romanian": "ro", "russian": "ru", "rwandan": "rw", "samoan": "ws",
    "kittitianNevis": "kn", "sammarinese": "sm", "vincentian": "vc", "saoTomean": "st", "saudi": "sa",
    "senegalese": "sn", "serbian": "rs", "seychellois": "sc", "sierraLeonean": "sl", "singaporean": "sg",
    "syrian": "sy", "somali": "so", "sriLankan": "lk", "swazi": "sz", "southAfrican": "za",
    "sudanese": "sd", "southKorean": "kr", "swedish": "se", "swiss": "ch", "surinamese": "sr",
    "southSudanese": "ss", "thai": "th", "tanzanian": "tz", "tajik": "tj", "timorese": "tl",
    "togolese": "tg", "tongan": "to", "trinidadian": "tt", "tunisian": "tn", "turkmen": "tm",
    "turkish": "tr", "tuvaluan": "tv", "ukrainian": "ua", "ugandan": "ug", "uruguayan": "uy",
    "uzbek": "uz", "vanuatuan": "vu", "venezuelan": "ve", "vietnamese": "vn", "usVirginIslander": "vi",
    "britishVirginIslander": "vg", "yemeni": "ye", "djiboutian": "dj", "zambian": "zm", "zimbabwean": "zw"
  };

  // Dictionnaire code ISO 3 lettres -> code ISO 2 lettres pour flagcdn (rétrocompatibilité)
  const isoCodeMap = {
    "afg": "af", "deu": "de", "and": "ad", "ago": "ao", "atg": "ag",
    "dza": "dz", "arg": "ar", "arm": "am", "aus": "au", "aut": "at",
    "aze": "az", "bhs": "bs", "bhr": "bh", "bgd": "bd", "brb": "bb",
    "bel": "be", "blz": "bz", "ben": "bj", "blr": "by", "mmr": "mm",
    "gnb": "gw", "bol": "bo", "bih": "ba", "bwa": "bw", "bra": "br",
    "boc": "gb", "brn": "bn", "bgr": "bg", "bfa": "bf", "bdi": "bi",
    "btn": "bt", "cpv": "cv", "khm": "kh", "cmr": "cm", "can": "ca",
    "qat": "qa", "caf": "cf", "tcd": "td", "cze": "cz", "chl": "cl",
    "chn": "cn", "cyp": "cy", "col": "co", "com": "km", "cog": "cg",
    "cod": "cd", "cri": "cr", "hrv": "hr", "cub": "cu", "dnk": "dk",
    "dom": "do", "dma": "dm", "ecu": "ec", "egy": "eg", "are": "ae",
    "eri": "er", "svk": "sk", "svn": "si", "esp": "es", "usa": "us",
    "est": "ee", "eth": "et", "phl": "ph", "fin": "fi", "fji": "fj",
    "fra": "fr", "gab": "ga", "gmb": "gm", "geo": "ge", "gha": "gh",
    "grd": "gd", "grc": "gr", "gtm": "gt", "gin": "gn", "guy": "gy",
    "hti": "ht", "nld": "nl", "hnd": "hn", "hun": "hu", "ind": "in",
    "eng": "gb", "idn": "id", "irn": "ir", "irq": "iq", "irl": "ie",
    "isl": "is", "isr": "il", "ita": "it", "jam": "jm", "jpn": "jp",
    "jor": "jo", "kaz": "kz", "ken": "ke", "kos": "xk", "kgz": "kg", "kir": "ki",
    "kwt": "kw", "lao": "la", "lso": "ls", "lva": "lv", "lbn": "lb",
    "lbr": "lr", "lby": "ly", "lie": "li", "ltu": "lt", "lux": "lu",
    "mkd": "mk", "mys": "my", "mwi": "mw", "mdg": "mg", "mli": "ml",
    "mlt": "mt", "civ": "ci", "mar": "ma", "mus": "mu", "mrt": "mr",
    "mex": "mx", "fsm": "fm", "mda": "md", "mco": "mc", "mng": "mn",
    "mne": "me", "moz": "mz", "nam": "na", "nzl": "nz", "npl": "np",
    "nic": "ni", "ner": "ne", "nga": "ng", "prk": "kp", "nor": "no",
    "omn": "om", "pak": "pk", "plw": "pw", "pan": "pa", "png": "pg",
    "pry": "py", "per": "pe", "pol": "pl", "prt": "pt", "pri": "pr",
    "reu": "re", "rou": "ro", "rus": "ru", "rwa": "rw", "wsm": "ws",
    "kna": "kn", "smr": "sm", "vct": "vc", "stp": "st", "sau": "sa",
    "sen": "sn", "srb": "rs", "syc": "sc", "sle": "sl", "sgp": "sg",
    "syr": "sy", "som": "so", "lka": "lk", "swz": "sz", "zaf": "za",
    "sdn": "sd", "kor": "kr", "swe": "se", "che": "ch", "sur": "sr",
    "ssd": "ss", "tha": "th", "tza": "tz", "tjk": "tj", "tls": "tl",
    "tgo": "tg", "ton": "to", "tto": "tt", "tun": "tn", "tkm": "tm",
    "tur": "tr", "tuv": "tv", "ukr": "ua", "uga": "ug", "ury": "uy",
    "uzb": "uz", "vut": "vu", "ven": "ve", "vnm": "vn", "isv": "vi",
    "vgb": "vg", "yem": "ye", "dji": "dj", "zmb": "zm", "zwe": "zw"
  };

  let flagUrl = null;
  const normalizedNat = nationality.trim().toLowerCase();

  // 1. Chercher d'abord par clé de traduction (ex: "french", "spanish")
  // Comparaison insensible à la casse pour gérer les clés en camelCase
  const flagCode = Object.keys(keyToFlagMap).find(
    key => key.toLowerCase() === normalizedNat
  );
  if (flagCode) {
    flagUrl = `https://flagcdn.com/${keyToFlagMap[flagCode]}.svg`;
  }

  // 2. Sinon, extraire le code ISO entre parenthèses (ex: "Español (ESP)" -> "esp")
  if (!flagUrl) {
    const isoMatch = nationality.match(/\(([A-Za-z]{3})\)/);
    if (isoMatch) {
      const isoCode3 = isoMatch[1].toLowerCase();
      const isoCode2 = isoCodeMap[isoCode3];
      if (isoCode2) {
        flagUrl = `https://flagcdn.com/${isoCode2}.svg`;
      }
    }
  }

  if (!flagUrl) {
    return null;
  }

  return (
    <Box sx={{ mb: 1 }}>
      <img
        src={flagUrl}
        alt={nationality}
        style={{
          width: size,
          height: 'auto',
          border: '1px solid #ccc',
          borderRadius: '2px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      />
    </Box>
  );
};

export default FlagDisplay;